/**
 * Registro do service worker e instalação do app.
 *
 * Duas correções sobre o comportamento atual:
 *
 * 1. **Limpar o cache no logout.** O `sw.js` antigo guardava `/api/auth/me` e
 *    `/api/admin/*`, e o logout só limpava o `localStorage` — em computador
 *    compartilhado, o próximo usuário lia os dados do anterior (S-07). Agora o app
 *    avisa o service worker, que apaga tudo. O `sw.js` novo também não cacheia API
 *    nenhuma, então há duas barreiras.
 *
 * 2. **Avisar quando há versão nova.** Sem isso o usuário fica preso numa versão
 *    antiga até fechar todas as abas, sem entender por que a correção não chegou.
 */

const EVENTO_ATUALIZACAO = 'autocomex:versao-nova'

/** Registra o service worker. Silencioso em desenvolvimento. */
export function registrarServiceWorker() {
  if (!('serviceWorker' in navigator)) return
  // Em desenvolvimento o SW atrapalha o recarregamento a quente.
  if (import.meta.env.DEV) return

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      .then((registro) => {
        // Versão nova encontrada: avisa em vez de trocar por baixo do usuário.
        registro.addEventListener('updatefound', () => {
          const novo = registro.installing
          if (!novo) return
          novo.addEventListener('statechange', () => {
            if (novo.state === 'installed' && navigator.serviceWorker.controller) {
              window.dispatchEvent(new Event(EVENTO_ATUALIZACAO))
            }
          })
        })
      })
      .catch(() => {
        // Falha no registro não impede o app de funcionar.
      })
  })
}

/**
 * Limpa o cache do service worker.
 *
 * Chamado no logout. Faz as duas pontas: manda a mensagem e, se der, apaga direto
 * — porque a mensagem depende de o SW estar ativo, e no logout não se pode contar
 * com isso.
 */
export async function limparCacheDoApp() {
  try {
    navigator.serviceWorker?.controller?.postMessage('LOGOUT')
  } catch {
    // ignorar
  }
  try {
    if ('caches' in window) {
      const chaves = await caches.keys()
      await Promise.all(chaves.map((k) => caches.delete(k)))
    }
  } catch {
    // ignorar
  }
}

/** Aplica a versão nova recarregando a página. */
export function aplicarAtualizacao() {
  window.location.reload()
}

export const EVENTO_VERSAO_NOVA = EVENTO_ATUALIZACAO

/* ------------------------------------------------------------- instalação */

/**
 * Evento de instalação do navegador.
 *
 * O navegador dispara `beforeinstallprompt` **uma vez** e só se o app for
 * instalável. Guardamos a referência porque o prompt só pode ser aberto a partir
 * de um gesto do usuário — mostrar um botão próprio é a única forma de oferecer a
 * instalação no momento em que ela faz sentido.
 */
interface EventoInstalacao extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

let eventoGuardado: EventoInstalacao | null = null

export function observarInstalacao(aoMudar: (podeInstalar: boolean) => void) {
  const capturar = (e: Event) => {
    // Sem isto o navegador mostra o próprio aviso, em momento arbitrário.
    e.preventDefault()
    eventoGuardado = e as EventoInstalacao
    aoMudar(true)
  }
  const instalado = () => {
    eventoGuardado = null
    aoMudar(false)
  }

  window.addEventListener('beforeinstallprompt', capturar)
  window.addEventListener('appinstalled', instalado)

  return () => {
    window.removeEventListener('beforeinstallprompt', capturar)
    window.removeEventListener('appinstalled', instalado)
  }
}

/** Abre o diálogo de instalação. Devolve `true` se o usuário aceitou. */
export async function instalarApp(): Promise<boolean> {
  if (!eventoGuardado) return false
  await eventoGuardado.prompt()
  const { outcome } = await eventoGuardado.userChoice
  // O evento é de uso único.
  eventoGuardado = null
  return outcome === 'accepted'
}

/** O app já está rodando instalado? */
export function rodandoInstalado(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // iOS não implementa `display-mode`.
    (navigator as { standalone?: boolean }).standalone === true
  )
}
